import React, { useState } from 'react';
import { Button, Icon } from '@iq/react-components';
import { useSelector } from 'react-redux';
import SimpleModal from '../../../SimpleModal/SimpleModal';
import { datetimeString, utcToSite } from '../../../../datetimeUtils';
import { getTimezone } from '../../../../bundles/application';
import { STATUSES } from '../../../../constants';
import IotEventModal from '../../../EventModal/IotEventModal';

function truncateNumber(num) {
  if (typeof num !== 'number' || num == null || Number.isNaN(num)) {
    return 'N/A';
  }
  if (num < 1) {
    return '<1';
  }
  const truncated = Math.trunc(num * 10) / 10;
  return truncated.toFixed(1);
}

export default function RiskModal({ openedRisk, setOpenedRisk, riskThreshold }) {
  const timezone = useSelector(getTimezone);
  const { color: statusColor, icon: statusIcon } = STATUSES[openedRisk.status] || {};
  const [r, g, b] = statusColor || [0, 0, 0];
  const [showEventModal, setShowEventModal] = useState(false);
  const {
    shutdownWarning = 60,
    shutdownEmergency = 80,
    downtimeWarning = 8,
    downtimeEmergency = 14,
  } = riskThreshold;

  const getValueColor = (k, t) => {
    if (k === 'Shutdown Risk') {
      if (t >= shutdownEmergency) {
        return '#ED4747';
      }
      if (t >= shutdownWarning) {
        return '#F9C846';
      }
      return '#666666';
    }
    if (k === 'Downtime Risk') {
      if (t >= downtimeEmergency) {
        return '#ED4747';
      }
      if (t >= downtimeWarning) {
        return '#F9C846';
      }
      return '#666666';
    }
    return null;
  };

  const formatEventDatetime = (timestamp, withMs = true) =>
    datetimeString(utcToSite(timestamp, timezone), timezone, withMs);

  return (
    <>
      {!showEventModal && (
        <SimpleModal
          size="m"
          onClose={() => setOpenedRisk()}
          className={`risk-modal-wrapper`}
          title={
            <div className={`risk-modal-header ${STATUSES[openedRisk.status].text.split(' ')[0]}`}>
              <Icon
                size="s"
                icon={statusIcon}
                style={{ color: `rgba(${r}, ${g}, ${b}, ${statusColor ? 1 : 0})` }}
              />
              {openedRisk.eventText || openedRisk.event.message || openedRisk.event.text}
            </div>
          }
        >
          <div className="risk-modal-body">
            <div className="risk-modal-left">
              <div className="risk-modal-attribute">
                <div className="attribute-label">Risk Level</div>
                <div className="attribute-value">{STATUSES[openedRisk.status].text}</div>
              </div>
              <div className="risk-modal-attribute">
                <div className="attribute-label">Item Designation</div>
                <div className="attribute-value">{openedRisk.itemDesignation}</div>
              </div>

              <div className="risk-modal-attribute">
                <div className="attribute-label">Point ID</div>
                <div className="attribute-value">{openedRisk.pointId}</div>
              </div>
              <div className="risk-modal-attribute">
                <div className="attribute-label">Point Group</div>
                <div className="attribute-value">
                  {openedRisk.event.remoteSource || openedRisk.event.pointGroup}
                </div>
              </div>
              <div className="risk-modal-attribute">
                <div className="attribute-label">Event Timestamp</div>
                <div className="attribute-value">
                  {formatEventDatetime(openedRisk.eventDateTime)}
                </div>
              </div>
            </div>
            <div className="risk-modal-right">
              <div className="risk-levels-container">
                <div className="risk-levels-label">
                  Additional Downtime Risk {'(hours)'}{' '}
                  <Button
                    design="text"
                    slim
                    icon={
                      <Icon
                        icon="he-info"
                        size="s"
                      />
                    }
                    tooltip={`The additional downtime risk represents an estimate of the expected downtime hours if the risk is not addressed in the considered time intervals. The considered time intervals are respectively 2 days, 1 week and 1 month.`}
                    onClick={(event) => event.preventDefault()}
                  ></Button>
                </div>
                <div className="risk-levels-undertext">
                  Indicated downtime risk was valid at the time when the alarm occured
                </div>
                <div className="risk-levels">
                  <div className="risk-level">
                    <div
                      className="risk-level-value"
                      style={{
                        border: `3px solid ${getValueColor('Downtime Risk', openedRisk.mttrDay)}`,
                      }}
                    >
                      {truncateNumber(openedRisk.mttrDay)}
                    </div>
                    <div className="risk-level-label">2 day</div>
                  </div>
                  <div className="risk-level">
                    <div
                      className="risk-level-value"
                      style={{
                        border: `3px solid ${getValueColor('Downtime Risk', openedRisk.mttrWeek)}`,
                      }}
                    >
                      {truncateNumber(openedRisk.mttrWeek)}
                    </div>
                    <div className="risk-level-label">1 week</div>
                  </div>
                  <div className="risk-level">
                    <div
                      className="risk-level-value"
                      style={{
                        border: `3px solid ${getValueColor('Downtime Risk', openedRisk.mttrMonth)}`,
                      }}
                    >
                      {truncateNumber(openedRisk.mttrMonth)}
                    </div>
                    <div className="risk-level-label">1 month</div>
                  </div>
                </div>
              </div>
              <div className="risk-modal-attribute">
                <div className="attribute-label">Source event</div>
                <div className="attribute-value">
                  <div
                    className="risk-modal-source-event"
                    onClick={() => setShowEventModal(true)}
                  >
                    {openedRisk.event.message}
                  </div>
                </div>
              </div>
              <div className="risk-modal-attribute">
                <div className="attribute-label">Descriptions & Mitigations</div>
                <div className="attribute-value description">{openedRisk.mitigation}</div>
              </div>
            </div>
          </div>
        </SimpleModal>
      )}
      {showEventModal && openedRisk.event && (
        <IotEventModal
          event={openedRisk.event}
          onBack={() => setShowEventModal(false)}
          onClose={() => {
            setShowEventModal(false);
            setOpenedRisk();
          }}
          hasHistory={true}
        ></IotEventModal>
      )}
    </>
  );
}
